import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { authApi } from '../services/api';
import { useAuth } from '../hooks/useApi';

const AccountSettingsPage: React.FC = () => {
  const { user } = useAuth();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const normalizedConfirm = deleteConfirmText
    .normalize('NFKC')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  const canDelete = normalizedConfirm === 'delete my account';

  const handleDeleteAccount = async () => {
    if (!canDelete) {
      setError('Please type "delete my account" to confirm');
      return;
    }

    setDeleting(true);
    setError(null);

    try {
      // Call backend to delete from both Neon and Supabase
      await authApi.deleteAccount();

      // Sign out from Supabase
      if (supabase) {
        try {
          await supabase.auth.signOut();
        } catch (err) {
          console.error('Error signing out:', err);
        }
      }

      // Clear local storage
      localStorage.clear();

      // Redirect to login with a message
      window.location.href = '/login?message=account-deleted';
    } catch (err: any) {
      console.error('Error deleting account:', err);
      const detail = err?.response?.data?.detail || err?.response?.data?.error;
      setError(detail || err?.message || 'Failed to delete account. Please try again or contact support.');
      setDeleting(false);
    }
  };

  if (!user) {
    return (
      <div className="auth-wrapper">
        <div className="auth-card">
          <h1 className="auth-title">Account Settings</h1>
          <p>Please sign in to manage your account settings.</p>
          <a href="/login" className="btn btn-primary" style={{ display: 'inline-block', marginTop: 16 }}>
            Sign In
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-wrapper" style={{ fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif' }}>
      <div className="auth-card">
        <div style={{ marginBottom: 32 }}>
          <h1 className="auth-title">Account Settings</h1>
          <p className="auth-subtitle">Manage your account preferences</p>
        </div>

        {/* Account Info Section */}
        <div style={{ marginBottom: 32, padding: 16, background: '#f3f4f6', borderRadius: 8 }}>
          <h3 style={{ marginBottom: 12, fontSize: 16, fontWeight: 600 }}>Account Information</h3>
          <div style={{ display: 'grid', gap: 8, fontSize: 14 }}>
            <div><strong>Username:</strong> {user.username}</div>
            <div><strong>Email:</strong> {user.email}</div>
            <div><strong>Member since:</strong> {new Date(user.createdAt).toLocaleDateString()}</div>
          </div>
        </div>

        {/* Danger Zone */}
        <div style={{
          padding: 20,
          border: '2px solid #dc2626',
          borderRadius: 8,
          background: '#fef2f2'
        }}>
          <h3 style={{
            color: '#dc2626',
            marginBottom: 12,
            fontSize: 18,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}>
            ⚠️ Danger Zone
          </h3>

          <p style={{ marginBottom: 16, fontSize: 14, lineHeight: 1.6 }}>
            Deleting your account is permanent and cannot be undone. We will anonymize your content where it’s part of other users’ conversations, and we may retain minimal account identifiers (e.g., email and auth ID) for legal/compliance purposes even though they are not visible in the product.
          </p>

          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              style={{
                background: '#dc2626',
                color: 'white',
                border: '2px solid #991b1b',
                padding: '10px 20px',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 600
              }}
            >
              Delete My Account
            </button>
          ) : (
            <div style={{ marginTop: 16 }}>
              <div style={{
                padding: 16,
                background: 'white',
                border: '2px solid #dc2626',
                borderRadius: 8,
                marginBottom: 16
              }}>
                <h4 style={{ marginBottom: 12, color: '#dc2626', fontWeight: 600 }}>
                  Are you absolutely sure?
                </h4>
                <p style={{ marginBottom: 16, fontSize: 14, lineHeight: 1.6 }}>
                  This action <strong>cannot be undone</strong>. This will:
                </p>
                <ul style={{ marginBottom: 16, paddingLeft: 24, fontSize: 14 }}>
                  <li>Remove your profile and sign-in access</li>
                  <li>Anonymize your topics/comments that have replies, so conversations stay intact</li>
                  <li>Remove topics without other participants</li>
                  <li>Keep minimal account identifiers for legal compliance (not shown to other users)</li>
                </ul>

                {error && (
                  <div style={{
                    background: '#fee2e2',
                    border: '1px solid #dc2626',
                    color: '#991b1b',
                    padding: 12,
                    borderRadius: 6,
                    marginBottom: 16,
                    fontSize: 14
                  }}>
                    {error}
                  </div>
                )}

                <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500 }}>
                  Type <strong>"delete my account"</strong> to confirm:
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="delete my account"
                  disabled={deleting}
                  style={{
                    width: '100%',
                    padding: 12,
                    border: '2px solid #d1d5db',
                    borderRadius: 6,
                    fontSize: 14,
                    marginBottom: 16,
                    boxSizing: 'border-box'
                  }}
                />

                <div style={{ display: 'flex', gap: 12 }}>
                  <button
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeleteConfirmText('');
                      setError(null);
                    }}
                    disabled={deleting}
                    style={{
                      flex: 1,
                      background: '#6b7280',
                      color: 'white',
                      border: 'none',
                      padding: '10px 20px',
                      borderRadius: 6,
                      cursor: deleting ? 'not-allowed' : 'pointer',
                      fontSize: 14,
                      fontWeight: 600,
                      opacity: deleting ? 0.5 : 1
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteAccount}
                    disabled={deleting || !canDelete}
                    style={{
                      flex: 1,
                      background: '#dc2626',
                      color: 'white',
                      border: 'none',
                      padding: '10px 20px',
                      borderRadius: 6,
                      cursor: (deleting || !canDelete) ? 'not-allowed' : 'pointer',
                      fontSize: 14,
                      fontWeight: 600,
                      opacity: (deleting || !canDelete) ? 0.5 : 1
                    }}
                  >
                    {deleting ? 'Deleting Account...' : 'Delete My Account Forever'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Back Button */}
        <div style={{ marginTop: 24, textAlign: 'center' }}>
          <a
            href="/"
            style={{
              color: '#3b82f6',
              textDecoration: 'none',
              fontSize: 14
            }}
          >
            ← Back to Home
          </a>
        </div>
      </div>
    </div>
  );
};

export default AccountSettingsPage;
